// Labels of the Gestiones IPC codes (04-diseno.md §4, design Prompt 0). The motive and closing motive catalogs are
// provisional (P5, P9): an unknown code is shown as the server sends it.

import { EMPTY, codeOf } from "../crmpostventa-shared/crmFormat";

export const CATEGORIAS = { IPC: "IPC", PROACTIVA: "PROACTIVA" };

export const TIPO_LABELS = {
  MODIFICACION: "Modificación",
  RECLAMO: "Reclamo",
  CONSULTA: "Consulta",
};

// Sections of the queue (server seccion) with the type or category they show and the key of their counter in
// the list response "secciones".
export const SECCIONES = [
  { value: "todas", label: "Todas" },
  { value: "modificacion", label: "Modificación", tipo: "MODIFICACION" },
  { value: "reclamo", label: "Reclamo", tipo: "RECLAMO" },
  { value: "consulta", label: "Consulta", tipo: "CONSULTA" },
  { value: "proactiva", label: "Proactiva", categoria: CATEGORIAS.PROACTIVA },
];

export const SECCION_TODAS = "todas";

export const seccionOf = (value) => SECCIONES.find((seccion) => seccion.value === value) || SECCIONES[0];

// Section of a gestión: Proactiva, or the section of its IPC type; Todas when the type is unknown.
export const seccionDeGestion = ({ categoria, tipo } = {}) => {
  if (codeOf(categoria) === CATEGORIAS.PROACTIVA) return "proactiva";
  return SECCIONES.find((seccion) => seccion.tipo && seccion.tipo === codeOf(tipo))?.value || SECCION_TODAS;
};

export const MOTIVO_LABELS = {
  DATOS_BANCARIOS: "Datos bancarios",
  DATOS_FISCALES: "Datos fiscales",
  TITULARIDAD: "Titularidad",
  DOMICILIO: "Domicilio",
  DATOS_TERMINAL: "Datos de terminal",
  OTROS: "Otros",
  LIQUIDACION: "Liquidación",
  SERVICIO_TECNICO: "Servicio técnico",
  CONFIGURACION_TERMINAL: "Configuración terminal",
  PLAZOS_ACREDITACION: "Plazos de acreditación",
  PANEL_CENTRAL: "Panel central",
  TASAS: "Tasas",
  OPERATIVA: "Operativa",
  SIMULADORES: "Simuladores",
  ORDENES_PAGO: "Órdenes de pago",
  CONSULTAS_ESPECIFICAS: "Consultas específicas",
  VISITA_COMERCIAL: "Visita comercial",
  OTRO: "Otro",
};

// Motive codes of each type, and of Proactiva (no type). A code may repeat across types (PLAZOS_ACREDITACION).
export const MOTIVOS = {
  MODIFICACION: ["DATOS_BANCARIOS", "DATOS_FISCALES", "TITULARIDAD", "DOMICILIO", "DATOS_TERMINAL", "OTROS"],
  RECLAMO: ["LIQUIDACION", "SERVICIO_TECNICO", "CONFIGURACION_TERMINAL", "PLAZOS_ACREDITACION", "PANEL_CENTRAL"],
  CONSULTA: ["TASAS", "PLAZOS_ACREDITACION", "OPERATIVA", "SIMULADORES", "ORDENES_PAGO", "CONSULTAS_ESPECIFICAS"],
  PROACTIVA: ["VISITA_COMERCIAL", "OTRO"],
};

export const ESTADO_LABELS = {
  PENDIENTE: "Pendiente",
  EN_GESTION: "En gestión",
  ESPERANDO_COMERCIO: "Esperando al comercio",
  DERIVADA: "Derivada",
  RESUELTA: "Resuelta",
  DESCARTADA: "Descartada",
};

// StatusChip tone of each state (design handoff: status-<tone>; waiting is the new violet token).
export const ESTADO_TONES = {
  PENDIENTE: "pending",
  EN_GESTION: "inprogress",
  ESPERANDO_COMERCIO: "waiting",
  DERIVADA: "derived",
  RESUELTA: "resolved",
  DESCARTADA: "discarded",
};

export const ESTADOS_ABIERTOS = ["PENDIENTE", "EN_GESTION", "ESPERANDO_COMERCIO", "DERIVADA"];
export const RESULTADOS = ["RESUELTA", "DESCARTADA"];

// Who has to act on an open gestión.
export const ACTORES = {
  PENDIENTE: "asesor",
  EN_GESTION: "asesor",
  ESPERANDO_COMERCIO: "comercio",
  DERIVADA: "area",
};

export const CANAL_LABELS = {
  WHATSAPP: "WhatsApp",
  LLAMADA: "Llamada",
  MAIL: "Mail",
  PRESENCIAL: "Presencial",
  OTRO: "Otro",
};

// Asesor today; Zoco Check and Sin facturación arrive later (R9).
export const ORIGEN_LABELS = {
  ASESOR: "Asesor",
  ZOCO_CHECK: "Zoco Check",
  SIN_FACTURACION: "Sin facturación",
};

export const MOTIVO_CIERRE_LABELS = {
  RESUELTO: "Resuelto",
  CLIENTE_CONTACTADO: "Cliente contactado",
  RESUELTO_POR_OTRA_AREA: "Resuelto por otra área",
  SIN_RESPUESTA: "Sin respuesta",
  NO_CORRESPONDE: "No corresponde",
  OTRO: "Otro",
};

export const toOptions = (labels, codes) => codes.map((code) => ({ value: code, label: labels[code] || code }));

// Motive of a row: the catalog label of its code, else the stored text of a histórico, else a dash.
export const motivoLabel = (item) => {
  const code = codeOf(item?.motivoCodigo);
  if (code) return MOTIVO_LABELS[code] || item.motivoCodigo;
  return String(item?.motivoTexto || "").trim() || EMPTY;
};

export const isProactiva = (item) => codeOf(item?.categoria) === CATEGORIAS.PROACTIVA;

// An open gestión of the module: históricos are read-only history even when they stayed open in the old system.
export const isOpenGestion = (item) => Boolean(item) && !item.esHistorico && item.activo !== false;

export const isMine = (item, currentUserId) =>
  currentUserId !== undefined &&
  currentUserId !== null &&
  item?.responsableUsuarioId !== undefined &&
  item?.responsableUsuarioId !== null &&
  Number(item.responsableUsuarioId) === Number(currentUserId);

// Rows of the module and histórico rows can share an id: the key is both.
export const rowKey = (item) => `${item.esHistorico ? "h" : "g"}-${item.id}`;

// Whole calendar days from `fromIso` to `toIso` (YYYY-MM-DD), negative when `toIso` is earlier.
export const daysBetween = (fromIso, toIso) => {
  if (!fromIso || !toIso) return null;
  const from = Date.UTC(Number(fromIso.slice(0, 4)), Number(fromIso.slice(5, 7)) - 1, Number(fromIso.slice(8, 10)));
  const to = Date.UTC(Number(toIso.slice(0, 4)), Number(toIso.slice(5, 7)) - 1, Number(toIso.slice(8, 10)));
  return Math.round((to - from) / 86400000);
};

// Empty states of each section (design Prompt 1).
export const EMPTY_ACTIVAS = {
  todas: "No hay gestiones activas",
  modificacion: "No hay modificaciones activas",
  reclamo: "No hay reclamos activos",
  consulta: "No hay consultas activas",
  proactiva: "No hay gestiones proactivas activas",
};

export const EMPTY_FINALIZADAS = {
  todas: "No hay gestiones finalizadas",
  modificacion: "No hay modificaciones finalizadas",
  reclamo: "No hay reclamos finalizados",
  consulta: "No hay consultas finalizadas",
  proactiva: "No hay gestiones proactivas finalizadas",
};
